import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/Layout';
import { useDealDetail } from '@/hooks/useDealDetail';
import { useBrandDeals } from '@/hooks/useBrandDeals';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, FileText, Users, CheckSquare, DollarSign, Calendar, MessageSquare, Loader2, UserCheck, ArrowRightLeft } from 'lucide-react';
import { ProfessionalDealOverview } from '@/components/deals/ProfessionalDealOverview';
import { BrandDealTaskManager } from '@/components/brand/BrandDealTaskManager';
import { BrandDealTalentTab } from '@/components/brand/BrandDealTalentTab';
import { BrandDealNegotiationsTab } from '@/components/brand/BrandDealNegotiationsTab';
import { useDealApplications } from '@/hooks/useDealApplications';
import { GanttChart } from '@/components/deals/GanttChart';
import { EnhancedDealMessaging } from '@/components/deals/EnhancedDealMessaging';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

const statusColors: Record<string, string> = {
  active: 'bg-gradient-to-r from-[hsl(var(--accent))]/20 to-[hsl(var(--success))]/20 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/30',
  pending: 'bg-gradient-to-r from-[hsl(var(--primary))]/20 to-[hsl(var(--info))]/20 text-[hsl(var(--info))] border border-[hsl(var(--info))]/30',
  completed: 'bg-[hsl(var(--secondary))]/20 text-[hsl(var(--secondary))] border border-[hsl(var(--secondary))]/30',
  cancelled: 'bg-destructive/10 text-destructive border border-destructive/30',
};

const DealDetailPage = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { deal, loading, error, fetchDeal } = useDealDetail(dealId);
  const { createTask, updateTask, deleteTask } = useBrandDeals();
  const { acceptApplication, rejectApplication } = useDealApplications(dealId);

  const handleAcceptTalent = async (dealId: string, talentProfileId: string) => {
    const app = (await supabase.from('deal_applications').select('id').eq('deal_id', dealId).eq('talent_profile_id', talentProfileId).eq('status', 'pending').maybeSingle()).data;
    if (app) {
      return acceptApplication(app.id);
    }
    // Fallback: directly update deal
    const { error } = await supabase.from('deals').update({ status: 'active', talent_profile_id: talentProfileId }).eq('id', dealId);
    if (error) throw error;
    fetchDeal();
    return { success: true };
  };

  const handleRejectTalent = async (dealId: string) => {
    const { error } = await supabase.from('deals').update({ status: 'available', talent_profile_id: null }).eq('id', dealId);
    if (error) throw error;
    fetchDeal();
    return { success: true };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[50vh]">
          <div className="flex items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-muted-foreground">Loading deal details...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !deal) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-destructive mb-2">Deal Not Found</h3>
              <p className="text-muted-foreground mb-4">{error || 'The deal you are looking for does not exist.'}</p>
              <Button onClick={() => navigate(-1)} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Map deal tasks to the format expected by components
  const mappedTasks = (deal.deal_tasks || []).map(task => ({
    id: task.id,
    deal_id: task.deal_id,
    title: task.title,
    description: task.description || '',
    status: task.status || 'pending',
    assigned_to: task.assigned_to || '',
    start_date: task.start_date,
    due_date: task.due_date || '',
    completion_date: task.completion_date || '',
    progress_percentage: task.progress_percentage || 0,
    depends_on_task_id: '',
    payment_amount: task.payment_amount,
    payment_status: task.payment_status as 'unpaid' | 'due' | 'paid' | undefined,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }));

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Breadcrumb Navigation */}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/brand-dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/deals-partnerships">Deals & Partnerships</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{deal.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-foreground">{deal.title}</h1>
                <Badge className={statusColors[deal.status || 'pending']}>
                  {deal.status}
                </Badge>
              </div>
              <p className="text-muted-foreground">
                {deal.talent?.name ? `${deal.talent.name} • ` : ''}
                {deal.deal_type} • {formatCurrency(deal.deal_value || 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content - Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          {/* h-auto so 7 tabs can wrap into 2 rows on mobile (grid-cols-4)
              without being clipped behind page content. */}
          <TabsList className="grid w-full grid-cols-4 md:grid-cols-7 gap-1 h-auto">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="talent" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              <span className="hidden sm:inline">Talent</span>
              {deal.status === 'pending' && (
                <Badge className="ml-1 bg-primary text-primary-foreground text-[10px] px-1 h-4">New</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="negotiations" className="flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Negotiations</span>
            </TabsTrigger>
            <TabsTrigger value="tasks" className="flex items-center gap-2">
              <CheckSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Tasks</span>
            </TabsTrigger>
            <TabsTrigger value="financials" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <span className="hidden sm:inline">Financials</span>
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">Timeline</span>
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Messages</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <ProfessionalDealOverview
              deal={{
                ...deal,
                status: deal.status || 'pending',
              }}
              primaryParty={{
                name: deal.brand?.company_name || "Brand Partner",
                type: "brand",
                details: "Marketing & Sponsorship Division"
              }}
              secondaryParty={{
                name: deal.talent?.name || "Talent Partner",
                type: "talent",
                details: deal.talent?.sport || "Professional Athlete"
              }}
              dealType={deal.deal_type || "sponsorship"}
            />
          </TabsContent>

          {/* Talent Tab */}
          <TabsContent value="talent">
            <BrandDealTalentTab
              deal={{
                id: deal.id,
                status: deal.status || 'pending',
                talent_profile_id: deal.talent_profile_id,
                updated_at: deal.updated_at || new Date().toISOString(),
              }}
              onAccept={handleAcceptTalent}
              onReject={handleRejectTalent}
              onRefresh={fetchDeal}
            />
          </TabsContent>

          {/* Negotiations Tab */}
          <TabsContent value="negotiations">
            <BrandDealNegotiationsTab
              dealId={deal.id}
              dealValue={deal.deal_value || 0}
              dealType={deal.deal_type || 'sponsorship'}
              estimatedCompletionDate={deal.estimated_completion_date || undefined}
              onActivated={fetchDeal}
            />
          </TabsContent>

          <TabsContent value="tasks">
            <BrandDealTaskManager
              dealId={deal.id}
              tasks={mappedTasks}
              onCreateTask={createTask}
              onUpdateTask={updateTask}
              onDeleteTask={deleteTask}
            />
          </TabsContent>

          {/* Financials Tab */}
          <TabsContent value="financials">
            <Card>
              <CardHeader>
                <CardTitle>Financial Overview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Total Deal Value</p>
                    <p className="text-2xl font-bold">{formatCurrency(deal.deal_value || 0)}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Paid Amount</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(
                        deal.deal_tasks?.filter(t => t.payment_status === 'paid')
                          .reduce((sum, t) => sum + (t.payment_amount || 0), 0) || 0
                      )}
                    </p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Remaining</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(
                        (deal.deal_value || 0) - 
                        (deal.deal_tasks?.filter(t => t.payment_status === 'paid')
                          .reduce((sum, t) => sum + (t.payment_amount || 0), 0) || 0)
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <Card>
              <CardHeader>
                <CardTitle>Deal Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                {mappedTasks.length > 0 ? (
                  <GanttChart tasks={mappedTasks} />
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    No tasks have been created yet. Add tasks to see the timeline.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <EnhancedDealMessaging dealId={deal.id} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default DealDetailPage;
